import type { FC, PropsWithChildren } from "react"
import { useRef } from "react"

import type { Point2d } from "@logic/lib/2d"
import type { DraggableData, DraggableEvent } from "react-draggable"
import { DraggableCore } from "react-draggable"
import { useRecoilState } from "recoil"

import { installAreasForDesignState } from "~/containers/Proposal/state/designs/design-state"

import { activityState } from "../../state/activity"

type PanProps = PropsWithChildren<{
  position: Point2d
  onDrag(position: Point2d): void
  nodeRef: React.RefObject<HTMLElement>
}>

export const Pan: FC<PanProps> = ({ children, position, onDrag, nodeRef }) => {
  const [activity, setActivity] = useRecoilState(activityState)
  const canPan = activity === null || activity === `panning`
  const displacement = useRef<Point2d>({ x: 0, y: 0 })
  const updatePosition = (e: DraggableEvent, d: DraggableData) => {
    if (canPan) {
      if (e instanceof MouseEvent) {
        const { movementX, movementY } = e
        const hasMoved = movementX || movementY
        if (hasMoved) {
          displacement.current.y += movementY
          displacement.current.x += movementX
          if (!activity) setActivity(`panning`)
        }
        onDrag({
          x: movementX + position.x,
          y: movementY + position.y,
        })
      }
      if (e instanceof TouchEvent) {
        const { deltaX, deltaY } = d
        const hasMoved = deltaX || deltaY
        if (hasMoved) {
          displacement.current.y += deltaY
          displacement.current.x += deltaX
          if (!activity) setActivity(`panning`)
        }
        onDrag({
          x: deltaX + position.x,
          y: deltaY + position.y,
        })
      }
    }
  }

  return (
    <>
      <DraggableCore
        nodeRef={nodeRef}
        onDrag={updatePosition}
        onStop={() => {
          if (displacement.current.x || displacement.current.y) {
            setActivity(null)
          }
          displacement.current.x = 0
          displacement.current.y = 0
        }}
      >
        {children}
      </DraggableCore>
    </>
  )
}
